export interface AuthOrganizationLimits {
    max_seats: number;
    max_clients: number;
    max_brand_applications: number;
    white_label_level: 'none' | 'basic' | 'full';
    can_publish_videos: boolean;
}

/** Estudio (o abogado independiente) del que el usuario es miembro. */
export interface AuthOrganization {
    id: number;
    name: string;
    slug: string;
    type: 'independent' | 'firm';
    type_display: string;
    status: 'active' | 'suspended';
    role: 'admin' | 'lawyer' | 'assistant';
    role_display: string;
    is_admin: boolean;
    is_owner: boolean;
    plan_name: string | null;
    plan_is_fallback: boolean;
    limits: AuthOrganizationLimits;
}

/** Marca que debe ver el usuario: la de su estudio, la del estudio que lo atiende o Monere. */
export interface AuthBranding {
    name: string;
    logo_url: string | null;
    primary_color: string;
    secondary_color: string;
    is_white_label: boolean;
}

export interface Auth {
    id?: number;
    firstName: string;
    lastName: string;
    email: string;
    avatar?: string;
    idNumber?: string;
    authToken?: string;
    hasAllPermissions?: boolean;
    roles?: string[];
    permissions?: string[];
    company?: string;
    organization?: AuthOrganization | null;
    branding?: AuthBranding | null;
}

/**
 * Convierte el payload de sesión del backend (login, registro, invitación) al modelo Auth.
 */
export const mapAuthResponse = (data: any): Auth => ({
    id: data.id,
    firstName: data.first_name,
    lastName: data.last_name,
    email: data.email,
    authToken: data.auth_token,
    avatar: data.avatar,
    roles: data.roles,
    permissions: data.permissions,
    hasAllPermissions: data.hasAllPermissions,
    organization: data.organization ?? null,
    branding: data.branding ?? null
});

/**
 * Obtiene el nombre del rol principal del usuario
 * @param auth - Objeto de autenticación
 * @returns El nombre del primer rol o 'Usuario' si no tiene roles
 */
export const getPrimaryRole = (auth: Auth | null): string => {
    if (!auth || !auth.roles || auth.roles.length === 0) {
        return 'Usuario';
    }
    // El independiente es "administrador" de su propia cuenta: se muestra como lo que es
    if (auth.organization?.type === 'independent') {
        return 'Abogado independiente';
    }
    return auth.roles[0];
};

/** True si el usuario tiene el permiso (codename) en alguno de sus roles. */
export const hasPermission = (auth: Auth | null | undefined, codename: string): boolean =>
    !!auth && (!!auth.hasAllPermissions || !!auth.permissions?.includes(codename));
